import { isBrowser } from '../utils/fragments/isBrowser';
import axios from './axios';

/**
 * Vue de sujet forum (graphdebate) — émetteur client.
 *
 * Pourquoi côté client : la vue était comptée dans le rendu serveur (resolver
 * GraphQL `findOrCreateThread`), donc UNE FOIS PAR RENDU. En ISR
 * stale-while-revalidate, Next sert la page depuis le cache ET la régénère en
 * arrière-plan → deux vues pour une seule visite (mesuré en prod le
 * 2026-07-31 : une requête, deux comptages à 271 ms d'écart, sur tous les
 * tenants GraphDebate). Un beacon navigateur est émis une fois par chargement
 * réel, quel que soit l'état du cache — et il porte enfin l'IP du lecteur
 * (le serveur ne voyait que l'egress de nos fronts) et son identité s'il est
 * connecté (l'instance axios pose `Authorization` et le header `website`).
 *
 * Best-effort : tout échec est silencieux (une vue perdue ne doit jamais
 * dégrader la page).
 */

/** Jeton de page-load, unique par (chargement × sujet) — cf. `vpid` du beacon GraphComment. */
const newViewPageId = (): string => {
  // Alphabet et longueur imposés par le contrat serveur : /^[a-zA-Z0-9_-]{10,64}$/.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  // `padEnd` : sur une valeur binaire ronde, `Math.random().toString(36)` peut
  // rendre moins de 10 caractères — le jeton serait alors rejeté en silence.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12).padEnd(10, '0')}`;
};

/**
 * Horodatage du dernier envoi par thread.
 *
 * Une fenêtre courte, et non un `Set` définitif : elle absorbe le double effet
 * de React StrictMode et les re-montages parasites, mais laisse recompter une
 * VRAIE revisite (sujet → liste → retour au sujet), qui est un chargement de
 * page réel — le comptage serveur la comptait, et le funnel d'engagement
 * (`topicFunnel`) se réarme à chaque montage : ne pas recompter la vue
 * casserait l'invariant `page ≥ has_read_comments`.
 */
const lastSentAt = new Map<string, number>();
const REARM_MS = 5000;

/**
 * Émetteur commun aux beacons de vue GraphDebate.
 *
 * ⚠ La clé de ré-armement inclut le chemin, pas seulement le thread : la page de
 * deep-link émet DEUX beacons pour le même `threadId` (une vue de sujet et une
 * ouverture de permalien). Une clé partagée ferait taire le second pendant 5 s —
 * la métrique « Liens directs » ne compterait alors quasiment plus rien, et le bug
 * serait invisible puisque l'échec est silencieux par conception.
 */
const sendGdViewBeacon = (path: string, threadId: string): void => {
  if (!isBrowser || !threadId) return;

  const key = `${path}:${threadId}`;
  const last = lastSentAt.get(key);
  if (last !== undefined && Date.now() - last < REARM_MS) return;
  lastSentAt.set(key, Date.now());

  const body = new URLSearchParams({ vpid: newViewPageId() });

  axios
    .post(path, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    .catch(() => {
      // Mesure best-effort : on rouvre la fenêtre pour qu'un prochain montage
      // puisse réessayer (avec un nouveau jeton). Un échec réseau ne doit
      // jamais dégrader la page.
      lastSentAt.delete(key);
    });
};

export const sendTopicView = (threadId: string): void =>
  sendGdViewBeacon(`/api/pub/gd-view/thread/${threadId}`, threadId);

/**
 * Ouverture de permalien (objet `yakli`, B7_01) — même raison d'être que ci-dessus.
 *
 * Le comptage serveur mesurait des RENDUS de la page de deep-link, robots inclus :
 * **693 510** événements comptés contre **559** vues humaines (Google Analytics) sur
 * 18 jours, soit ×1 241. Il existe une URL de permalien par commentaire (3,58 M sur
 * psychologies) contre une par sujet (56 822) : l'espace crawlable est 63× plus grand,
 * d'où un écart sans commune mesure avec celui des vues de sujet.
 */
export const sendYakliView = (threadId: string): void =>
  sendGdViewBeacon(`/api/pub/gd-view/yakli/thread/${threadId}`, threadId);
